import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/ApiService';
import { snapshotService } from '../services/SnapshotService';
import type { GalleryItem as ApiGalleryItem } from '../services/ApiService';

interface GalleryContextData {
    galleryItems: ApiGalleryItem[];
    loading: boolean;
    error: string | null;
    refreshGallery: () => Promise<void>;
    // Loading states for individual items
    loadingItems: Set<string>;
    setItemLoading: (itemId: string, loading: boolean) => void;
    // Navigation loading state
    navigationLoading: boolean;
    setNavigationLoading: (loading: boolean) => void;
    // Image preloading
    preloadedImages: Set<string>;
    preloadImage: (url: string) => Promise<void>;
}

const GalleryContext = createContext<GalleryContextData | undefined>(undefined);

interface GalleryProviderProps {
    children: ReactNode;
}

export const GalleryProvider: React.FC<GalleryProviderProps> = ({ children }) => {
    const [galleryItems, setGalleryItems] = useState<ApiGalleryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
    const [navigationLoading, setNavigationLoading] = useState(false);
    const [preloadedImages, setPreloadedImages] = useState<Set<string>>(new Set());

    const loadGalleryItems = async () => {
        // Only load if we haven't loaded yet
        if (hasLoaded || loading) {
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            // Try snapshot service first, fallback to API
            const response = await snapshotService.loadGalleryData(() => apiService.getGalleryItems());
            setGalleryItems(response.items);
            setHasLoaded(true);
        } catch (err) {
            console.error('Failed to load gallery items:', err);
            setError(err instanceof Error ? err.message : 'Failed to load gallery items');
            setHasLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const refreshGallery = async () => {
        setHasLoaded(false);
        await loadGalleryItems();
    };

    const setItemLoading = (itemId: string, loading: boolean) => {
        setLoadingItems(prev => {
            const newSet = new Set(prev);
            if (loading) {
                newSet.add(itemId);
            } else {
                newSet.delete(itemId);
            }
            return newSet;
        });
    };

    const preloadImage = async (url: string): Promise<void> => {
        if (preloadedImages.has(url)) {
            return;
        }

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                setPreloadedImages(prev => new Set(prev).add(url));
                resolve();
            };
            img.onerror = reject;
            img.src = url;
        });
    };

    useEffect(() => {
        loadGalleryItems();
    }, []);

    const contextValue: GalleryContextData = {
        galleryItems,
        loading,
        error,
        refreshGallery,
        loadingItems,
        setItemLoading,
        navigationLoading,
        setNavigationLoading,
        preloadedImages,
        preloadImage,
    };

    return (
        <GalleryContext.Provider value={contextValue}>
            {children}
        </GalleryContext.Provider>
    );
};

export const useGallery = (): GalleryContextData => {
    const context = useContext(GalleryContext);
    if (context === undefined) {
        // Return default values instead of throwing error to handle timing issues
        return {
            galleryItems: [],
            loading: false,
            error: null,
            refreshGallery: async () => {},
            loadingItems: new Set(),
            setItemLoading: () => {},
            navigationLoading: false,
            setNavigationLoading: () => {},
            preloadedImages: new Set(),
            preloadImage: async () => {},
        };
    }
    return context;
};
